import React, { useState, useEffect, useCallback } from 'react';
import { Download, Send, Info } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiErrorDetail } from '../../lib/adminApi';

const inputCls = 'w-full border border-[#E6E2DC] px-4 py-2.5 text-sm focus:outline-none focus:border-[#DE92A9] bg-white';
const labelCls = 'block text-xs tracking-wider uppercase text-[#757B7F] mb-1.5';

export default function NewsletterManager() {
  const [subscribers, setSubscribers] = useState([]);
  const [history, setHistory] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get('/api/admin/subscribers').then((res) => setSubscribers(res.data)).catch((err) => console.error('Failed to load subscribers:', err));
    api.get('/api/admin/newsletter/history').then((res) => setHistory(res.data)).catch((err) => console.error('Failed to load newsletter history:', err));
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get('/api/admin/subscribers/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'subscribers.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const active = subscribers.filter((s) => s.active !== false).length;
    if (!window.confirm(`Send this newsletter to ${active} active subscriber${active === 1 ? '' : 's'}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post('/api/admin/newsletter/send', { subject, message });
      if (data.failed && data.failed.length > 0) {
        toast.warning(`Sent to ${data.sent} of ${data.total}. ${data.failed.length} failed (see note below about Resend domain verification).`);
      } else {
        toast.success(`Newsletter sent to ${data.sent} subscriber${data.sent === 1 ? '' : 's'}`);
      }
      setSubject('');
      setMessage('');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="newsletter-manager">
      {/* Compose */}
      <div>
        <form onSubmit={send} className="bg-white border border-[#E6E2DC]/60 p-8 space-y-5" data-testid="newsletter-form">
          <h2 className="text-2xl text-[#2C2C2C]" style={{ fontFamily: '"Playfair Display", serif' }}>Send Newsletter</h2>
          <div>
            <label className={labelCls}>Subject *</label>
            <input className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} required placeholder="Our Summer 2026 issue is here!" data-testid="newsletter-subject-input" />
          </div>
          <div>
            <label className={labelCls}>Message *</label>
            <textarea className={inputCls} rows={9} value={message} onChange={(e) => setMessage(e.target.value)} required placeholder="Write your newsletter... each line becomes a paragraph in the branded email template." data-testid="newsletter-message-input" />
          </div>
          <button type="submit" disabled={sending} className="inline-flex items-center gap-2 text-white text-xs tracking-[0.15em] font-semibold px-8 py-3 hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#DE92A9' }} data-testid="newsletter-send-btn">
            <Send size={13} /> {sending ? 'SENDING...' : 'SEND TO ALL SUBSCRIBERS'}
          </button>
          <p className="flex items-start gap-2 text-[11px] text-[#757B7F] leading-relaxed">
            <Info size={13} className="shrink-0 mt-0.5 text-[#DE92A9]" />
            Emails are sent via Resend from onboarding@resend.dev. To reach every subscriber reliably, verify your own domain in your Resend account and I can switch the sender address.
          </p>
        </form>

        {history.length > 0 && (
          <div className="bg-white border border-[#E6E2DC]/60 p-6 mt-6" data-testid="newsletter-history">
            <h3 className="text-sm font-medium text-[#2C2C2C] mb-3">Recently sent</h3>
            <ul className="space-y-2">
              {history.map((h) => (
                <li key={h.id} className="text-xs text-[#757B7F] flex justify-between gap-3">
                  <span className="truncate text-[#2C2C2C]">{h.subject}</span>
                  <span className="shrink-0">{h.sent} sent{h.failed ? `, ${h.failed} failed` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Subscribers */}
      <div className="bg-white border border-[#E6E2DC]/60 p-8" data-testid="subscribers-panel">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-2xl text-[#2C2C2C]" style={{ fontFamily: '"Playfair Display", serif' }}>
            Subscribers <span className="text-sm text-[#DE92A9]">{subscribers.length}</span>
          </h2>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 border border-[#E6E2DC] text-xs tracking-wider px-4 py-2 text-[#757B7F] hover:border-[#DE92A9]" data-testid="subscribers-export-btn">
            <Download size={13} /> EXPORT CSV
          </button>
        </div>
        <div className="max-h-[480px] overflow-y-auto divide-y divide-[#E6E2DC]/60">
          {subscribers.map((s) => (
            <div key={s.email} className="py-2.5 flex justify-between gap-3 text-xs">
              <span className="text-[#2C2C2C] truncate">{s.email}</span>
              <span className={`shrink-0 ${s.active === false ? 'text-red-400' : 'text-[#757B7F]'}`}>
                {s.active === false ? 'inactive' : (s.subscribed_at || '').slice(0, 10)}
              </span>
            </div>
          ))}
          {subscribers.length === 0 && <p className="text-sm text-[#757B7F] py-4">No subscribers yet. The subscribe form is in the site footer.</p>}
        </div>
      </div>
    </div>
  );
}
